import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input, OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';

/**
 * A simple component to show soho's dismissible tags. Used largely
 *
 */
@Component({
  selector: 'lm-list-searchtags',
  templateUrl: './lm-list-searchtags.component.html',
  styleUrls: ['lm-list-searchtags.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LMListSearchTagsComponent implements OnInit {
  @Input() iconName: string = 'close';
  @Input() showRefineAndClear: boolean = true;
  @Input() idPrefix: string;
  @Input() automationIdPrefix: string;

  /**
   * List of search tags that will be displayed in this component.
   * @type {Array<LMListSearchTag>}
   */
  @Input() searchTags: Array<LMListSearchTag> = [];

  /**
   * Event to notify when a tag has been removed via its close button.
   * @type {EventEmitter<LMListSearchTag>}
   */
  @Output() searchTagRemoved = new EventEmitter<LMListSearchTag>();

  /**
   * Event to notify when clear/refine tag has been clicked.
   * @type {EventEmitter<void>}
   */
  @Output() clearFilter = new EventEmitter<void>();
  @Output() refineFilter = new EventEmitter<void>();

  /**
   * Internal host binding variable for this elements max height
   * This is meant to keep the list component from getting to tall.
   */
  @HostBinding('style.maxHeight.px') maxHeight = this.getMaxHeight();

  @HostBinding('attr.data-automation-id') automationId;


  /** Set search form max height to 40% of given space on resize */
  @HostListener('window:resize') public onResize() {
    this.maxHeight = this.getMaxHeight();
  }

  @ViewChildren('SearchTag') private searchTagButtons: QueryList<ElementRef>;

  public focusedIndex: number;
  public focusedTagId: string;

  constructor(
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.automationId = `lm-${this.automationIdPrefix}-search-tags`;
  }

  /** internal event handler function for use in template */
  onRemoveTag(index: number) {
    // ----------------------------------------------------------------
    // copy off searchTag to be removed so it can be sent in the
    // searchTagRemovedEvent emitter
    // ----------------------------------------------------------------
    const removedSearchTag: LMListSearchTag = Object.assign({}, this.searchTags[index]);

    this.searchTags.splice(index, 1);
    this.searchTagRemoved.emit(removedSearchTag);


    if (this.focusedIndex >= this.searchTags.length) {
      this.setFocusedTag(this.searchTags[this.searchTags.length - 1]?.id);
    } else {
      this.setFocusedTag(this.searchTags[this.focusedIndex]?.id);
    }

    if (this.focusedIndex >= 0) {
      this.setFocusToElement(this.focusedTagId);
    }
  }

  onClearFilter() {
    this.clearFilter.emit();
  }

  onRefineFilter() {
    this.refineFilter.emit();
  }

  private getMaxHeight(): number {
    const thisElement = this.elementRef.nativeElement;
    const lmList = thisElement.closest('lm-list');

    if (lmList) {
      const containerHeight = lmList.clientHeight;
      return Math.round(containerHeight * .35);
    } else {
      return null;
    }
  }

  trackById(index: number, searchTag: LMListSearchTag): string {
    return searchTag.id;
  }

  setFocusedTag(tagId: string) {
    this.focusedIndex = tagId ? this.searchTags.findIndex(searchTag => searchTag.id === tagId) : -1;
    this.focusedTagId = tagId;
  }

  isFocused(tagId: string) {
    return tagId === this.focusedTagId;
  }

  private setFocusToElement(tagId: string) {
    if (tagId) {
      const elementToFocus = this.searchTagButtons?.toArray().find((searchTag) => searchTag.nativeElement.dataset.tagid === tagId);
      if (elementToFocus?.nativeElement) {
        elementToFocus.nativeElement.focus();
      }
    }
  }
}
